import React, { ReactElement, useRef } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import {
  Button,
  IconButton,
  InputAdornment,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
} from '@material-ui/core';
import { Field, formValueSelector } from 'redux-form/immutable';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';

import FormCheckbox from 'retired/FormCheckbox';
import Tooltip from 'retired/Tooltip';
import { TableBox } from 'retired/TableBox';
import LinkTab from 'retired/LinkTab';
import TablePaginationActions from 'retired/TablePaginationActions';
import { selectOrganization } from 'entities/user/selectors';
import { selectDisplayedDonations, selectTotal } from 'entities/donation/selectors';
import { selectDonationMatchingRules } from 'entities/donationMatchingRule/selectors';
import Donation from 'entities/donation/model';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import User from 'entities/user/model';

import DonationsTable from './DonationsTable';
import DonationsFilter from './DonationsFilter';
import * as Actions from './actions';
import { selectFetchParams, selectLoading, selectSelectedDonations } from './selectors';
import { FORM_IDENTIFIER } from './constants';

type Props = {
  user: User;
};
export const DonationsList = ({ user }: Props): ReactElement => {
  const match = useRouteMatch();

  const selector = formValueSelector(FORM_IDENTIFIER);
  const state = useSelector((s) => s);
  const matchingMode = selector(state, 'matching');
  const donations = useSelector(selectDisplayedDonations);
  const loading = useSelector(selectLoading);
  const totalCount = useSelector(selectTotal);
  const organization = useSelector(selectOrganization);
  const fetchParams = useSelector(selectFetchParams);
  const selectedDonations = useSelector(selectSelectedDonations);
  const donationMatchingRules = useSelector(selectDonationMatchingRules);

  const dispatch = useDispatch();
  const searchInputRef = useRef();

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(Actions.setFetchParamsAction({ pageNum }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      Actions.setFetchParamsAction({
        limit: Number(event.target.value),
      })
    );
  };

  // Since React synthetic events are reused we should extract
  // the event value outside of the debounced function
  const handleSearch = (search: string): void => {
    dispatch(Actions.setFetchParamsAction({ search }));
  };

  const handleSearchDebounced = debounce((search) => handleSearch(search), 600);

  const sortTableBy = (column: string, reserve = false): void => {
    const defaultOrder = reserve ? 'asc' : 'desc';
    const reverseOrder = reserve ? 'desc' : 'asc';
    dispatch(
      Actions.setFetchParamsAction({
        sort: column,
        order:
          fetchParams.get('sort') !== column || fetchParams.get('order') === reverseOrder
            ? defaultOrder
            : reverseOrder,
      })
    );
  };

  const isCompany = organization?.isCompany();

  // match.params.type === 'donors' represents the employee tab
  const viewIsDonors =
    user.hasPrivilege(Privileges.DONATION_INDIVIDUALS_VIEW) &&
    (!match.params.type || match.params.type === 'donors');
  const viewIsCorporateDonations = match.params.type === 'corporate';
  const viewIsCompanyDonationOnUserBehalf = match.params.type === 'company-donation-on-user-behalf';

  const matchingEnabled =
    isCompany &&
    viewIsDonors &&
    user.hasPrivilege(Privileges.DONATION_MATCH) &&
    user.hasFeature(Features.DONATION_MATCHING);

  const canViewDonationMatching = viewIsDonors && user.hasPrivilege(Privileges.DONATION_MATCH);

  const recurringEnabled =
    isCompany &&
    !viewIsCorporateDonations &&
    !viewIsCompanyDonationOnUserBehalf &&
    user.hasPrivilege(Privileges.DONATION_VIEW) &&
    user.hasFeature(Features.RECURRING_DONATIONS);

  const showCorporateDonation =
    (user?.hasFeature(Features.CORPORATE_DONATIONS) &&
      user?.hasPrivilege(Privileges.DONATION_CORPORATE_DONATE)) ||
    (user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS) &&
      user?.hasPrivilege(Privileges.DONATION_CORPORATE_LOG));

  const showVolunteerRewardDonation =
    user?.hasFeature(Features.VOLUNTEER_REWARD) &&
    user?.hasPrivilege(Privileges.VOLUNTEER_REWARDS_MANAGE);

  const useAdvancedReporting = user?.hasFeature(Features.ADVANCED_REPORTING);

  const renderTableHead = (): ReactElement => (
    <TableHead>
      <TableRow>
        {matchingEnabled && (
          <TableCell size="medium" style={{ width: 88 }}>
            Select
          </TableCell>
        )}
        {isCompany && (
          <TableCell size="medium">
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'currency'}
              onClick={(): void => sortTableBy('currency')}
            >
              Currency
            </TableSortLabel>
          </TableCell>
        )}
        <TableCell size="medium" style={{ textAlign: 'center' }}>
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'amount'}
            onClick={(): void => sortTableBy('amount', true)}
          >
            Donation
          </TableSortLabel>
        </TableCell>
        {(matchingEnabled || canViewDonationMatching) && (
          <TableCell size="medium" style={{ textAlign: 'center' }}>
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'matchedAmount'}
              onClick={(): void => sortTableBy('matchedAmount', true)}
            >
              {matchingMode ? 'Best Match' : 'Matched'}
            </TableSortLabel>
          </TableCell>
        )}
        <TableCell size="medium">
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'date'}
            onClick={(): void => sortTableBy('date', true)}
          >
            Donated&nbsp;on
          </TableSortLabel>
        </TableCell>
        {!viewIsCorporateDonations && (
          <TableCell size="medium">
            {viewIsCompanyDonationOnUserBehalf ? 'Employee' : 'Donor'}
          </TableCell>
        )}
        {viewIsCorporateDonations && <TableCell size="medium">Submitted&nbsp;by</TableCell>}
        {matchingMode && (
          <TableCell size="medium" style={{ textAlign: 'center' }}>
            Matching Cap Balance
          </TableCell>
        )}
        {isCompany && (
          <TableCell size="medium">
            {viewIsCompanyDonationOnUserBehalf && 'Designated '}Nonprofits
          </TableCell>
        )}
        {viewIsCompanyDonationOnUserBehalf && (
          <>
            <TableCell size="medium">Volunteer Date</TableCell>
            <TableCell size="medium">Volunteer Duration</TableCell>
            <TableCell size="medium">Event</TableCell>
          </>
        )}
        {viewIsDonors && <TableCell size="medium">Deed</TableCell>}

        <TableCell size="medium">
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'provider'}
            onClick={(): void => sortTableBy('provider')}
          >
            Processor
          </TableSortLabel>
        </TableCell>
        {matchingEnabled && !matchingMode && (
          <TableCell size="medium">
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'matchingProvider'}
              onClick={(): void => sortTableBy('matchingProvider', true)}
            >
              Matching Processor
            </TableSortLabel>
          </TableCell>
        )}
        {recurringEnabled && (
          <TableCell size="medium">
            <TableSortLabel
              active={fetchParams.get('sort') === 'donationSchedule.frequency'}
              onClick={(): void => sortTableBy('donationSchedule.frequency', true)}
            >
              Recurring Frequency
            </TableSortLabel>
          </TableCell>
        )}
        {viewIsCorporateDonations && <TableCell size="medium">Notes</TableCell>}
        {viewIsCorporateDonations && <TableCell size="medium">Action</TableCell>}
      </TableRow>
    </TableHead>
  );

  return (
    <>
      {user?.hasFeature(Features.DONATIONS) && showCorporateDonation && (
        <div style={{ marginBottom: '1.33em' }}>
          {(!isCompany || user?.hasPrivilege(Privileges.DONATION_INDIVIDUALS_VIEW)) && (
            <LinkTab path="/donations/donors" label={isCompany ? 'Employees' : 'Donors'} />
          )}
          {showCorporateDonation && <LinkTab path="/donations/corporate" label="Corporate" />}
          {showVolunteerRewardDonation && (
            <LinkTab path="/donations/company-donation-on-user-behalf" label="Volunteer Grant" />
          )}
        </div>
      )}
      <DonationsFilter
        matchingEnabled={matchingEnabled}
        matchingMode={matchingMode}
        recurringEnabled={recurringEnabled}
        user={user}
        type={match.params.type}
      />

      <TableBox
        loading={loading}
        fixedTop={
          <Toolbar style={{ marginTop: -4 }}>
            {matchingEnabled && (
              <Tooltip title={selectedDonations.size === 0 && 'No donations selected'}>
                <span>
                  {!matchingMode ? (
                    <Button
                      onClick={(): void => {
                        dispatch(Actions.openOverviewAction());
                      }}
                      variant="contained"
                      disabled={!(selectedDonations.size > 0)}
                      size="small"
                      title=""
                      style={{
                        marginRight: 16,
                        paddingLeft: 16,
                        paddingRight: 16,
                      }}
                    >
                      Review Unmatching for {selectedDonations.size} Donation
                      {selectedDonations.size === 1 ? '' : 's'}
                    </Button>
                  ) : (
                    <Button
                      onClick={(): void => {
                        dispatch(Actions.openOverviewAction());
                      }}
                      variant="contained"
                      disabled={!(selectedDonations.size > 0)}
                      size="small"
                      style={{
                        marginRight: 16,
                        paddingLeft: 16,
                        paddingRight: 16,
                      }}
                    >
                      Review Matching for {selectedDonations.size} Donation
                      {selectedDonations.size === 1 ? '' : 's'}
                    </Button>
                  )}
                </span>
              </Tooltip>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginLeft: 'auto',
              }}
            >
              {matchingEnabled && (
                <Field
                  name="matching"
                  component={FormCheckbox}
                  label="Matching mode"
                  value="matching"
                />
              )}
              <TextField
                type="search"
                size="small"
                placeholder="Search"
                inputRef={searchInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear"
                        onClick={(): void => {
                          if (searchInputRef.current) {
                            searchInputRef.current.value = '';
                          }
                          handleSearch('');
                        }}
                        size="small"
                        disabled={!fetchParams.get('search')}
                      >
                        <ClearIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  handleSearchDebounced(event.target.value)
                }
                style={{
                  width: 350,
                  marginLeft: 10,
                }}
              />
            </div>
          </Toolbar>
        }
        fixedBottom={
          <>
            <div style={{ color: '#0000008a', fontSize: '0.875rem' }}>
              {useAdvancedReporting ? (
                <>
                  <Tooltip
                    placement="top-start"
                    title="To view and download donations totals and data, click the link to the Donations Report."
                  >
                    <span>
                      <InfoIcon />
                      &nbsp;
                    </span>
                  </Tooltip>
                  <span>
                    Visit <a href="/reports/donationsComprehensive">Donations Report</a> for details
                    & exports.
                  </span>
                </>
              ) : null}
            </div>
            <TablePagination
              component="div"
              count={totalCount}
              rowsPerPage={fetchParams.get('limit')}
              page={fetchParams.get('pageNum')}
              onChangePage={goToPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </>
        }
      >
        <DonationsTable
          donations={
            matchingMode
              ? donations.filter(
                  (donation: Donation) => donation.matchableRule && !donation.isExternal()
                )
              : donations
          }
          header={renderTableHead()}
          isCompany={isCompany}
          viewIsCorporateDonations={viewIsCorporateDonations}
          viewIsCompanyDonationOnUserBehalf={viewIsCompanyDonationOnUserBehalf}
          viewIsDonors={viewIsDonors}
          canViewDonationMatching={canViewDonationMatching}
          matchingEnabled={matchingEnabled}
          matchingMode={matchingMode}
          recurringEnabled={recurringEnabled}
          organization={organization}
          selectedDonations={selectedDonations}
          selectDonation={(id: string): void => {
            dispatch(Actions.selectDonationAction(id));
          }}
          donationMatchingRules={donationMatchingRules}
          user={user}
          loading={loading}
          cancelCorporateDonation={(corporateDonationId: string): void => {
            dispatch(Actions.cancelCorporateDonationAction(corporateDonationId));
          }}
        />
      </TableBox>
    </>
  );
};
